// Live preview iframe.
//
// Per PRINT_TEMPLATE_PLAN.md §6 Phase 7: shows the active builder
// template rendered against the per-type sample fixture. Re-renders
// debounced 500 ms after `templateContent` changes so the user sees
// their edits within roughly half a second without hammering the
// server on every keystroke.
//
// UX rules:
//   - First mount: show a centred spinner inside the iframe wrapper.
//   - Subsequent renders while loading: keep the previous iframe
//     visible AND overlay a small spinner — preserving the stale
//     preview is less jarring than a flash to white.
//   - Errors: show an inline error block + "Retry" button that
//     re-fires the mutation against the current templateContent.
//
// Per Q4 of plan §9, the iframe is loaded via `srcdoc` (not a Blob
// object URL) — one fetch → one render → one srcdoc.

import { useEffect, useRef, useState } from 'react';

import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { Button } from '../../../../components/ui/Button/Button';
import { useRenderPrintTemplate } from '../hooks/usePrintTemplates';
import type {
  PrintTemplateContent,
  PrintTemplateType,
} from '../../../../types/api';
import styles from './LivePreview.module.css';

const DEBOUNCE_MS = 500;

export interface LivePreviewProps {
  templateType: PrintTemplateType;
  templateContent: PrintTemplateContent;
}

export function LivePreview({
  templateType,
  templateContent,
}: LivePreviewProps): JSX.Element {
  const render = useRenderPrintTemplate();
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the latest content so the debounced timer always renders the
  // most recent value, not whatever value was current when it was set.
  const contentRef = useRef(templateContent);
  contentRef.current = templateContent;

  function fireRender() {
    render.mutate(
      {
        type: templateType,
        // Per plan §6.4 the live preview always passes `?sample=1`
        // semantically — the server-side sample fixture is used in
        // place of any caller context.
        context: { sample: true },
        format: 'html',
      },
      {
        onSuccess: (out) => {
          if (out.html !== null) setHtml(out.html);
          setError(null);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to render preview');
        },
      },
    );
  }

  // Initial render on mount + every templateContent change (debounced).
  useEffect(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fireRender();
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // We intentionally rerun on templateContent and templateType only.
    // `fireRender` is recreated each render but reads templateType
    // and contentRef.current; listing it as a dep would loop.
  }, [templateContent, templateType]);

  function handleRetry() {
    setError(null);
    fireRender();
  }

  const showSpinnerOnly = html === null && error === null;

  return (
    <div className={styles.wrap}>
      <div className={styles.frameSlot}>
        {html !== null ? (
          <iframe
            title="Print preview"
            sandbox="allow-same-origin"
            srcDoc={html}
            className={styles.frame}
          />
        ) : null}
        {showSpinnerOnly ? (
          <div className={styles.fullSpinner}>
            <Spinner label="Rendering preview" />
          </div>
        ) : null}
        {render.isPending && html !== null ? (
          <div className={styles.overlaySpinner} aria-live="polite">
            <Spinner size="sm" label="Updating preview" />
          </div>
        ) : null}
      </div>
      {error !== null ? (
        <div className={styles.errorBlock} role="alert">
          <p className={styles.errorText}>
            Could not render the preview. {error}
          </p>
          <Button variant="primary" onClick={handleRetry}>
            Retry
          </Button>
        </div>
      ) : null}
    </div>
  );
}
